import React, { useEffect, useMemo, useState } from 'react'
import Hero from './hero-team-subpage'
import Introduction from './introduction-team-new'
import InvestmentsTable from './investments-table'
import FeaturedInvestments from './featured-investments'
// import NewsPostPreviewGrid from '../components/news-post-preview-grid'
import { Textfit } from 'react-textfit'
import { useMediaQuery } from 'react-responsive'
import BlockContent from '@sanity/block-content-to-react'

import styles from './person.module.css'
import heroStyles from './hero.module.css'
import PersonArticles from './PersonFeaturing/PersonArticles'
import Video from './video/video'
import headerStyles from './header.module.css'
import Icon from './icons'
import { Image } from './Image'
import { Link } from 'gatsby'

const getSocialLinks = ({
  socialTwitter,
  socialLinkedin,
  socialInstagram,
  socialSubstack
}) => ([
  socialTwitter && { label: 'Twitter', href: socialTwitter },
  socialLinkedin && { label: 'LinkedIn', href: socialLinkedin },
  socialInstagram && { label: 'Instagram', href: socialInstagram },
  socialSubstack && { label: 'Substack', href: socialSubstack }
].filter(Boolean))

const getFeaturedCompanies = (content = [], showExtended = false) => {
  if (!content.length) return []

  const uniqueItems = content.reduce((acc, current) => {
    const currentTitle = current?.company?.title
    const hasMatch = acc.find(item => item?.company?.title === currentTitle)

    if (!hasMatch) {
      acc.push(current)
    }

    return acc
  }, [])

  const sorted = [...uniqueItems].sort((firstItem, secondItem) => {
    if (firstItem.order > secondItem.order) {
      return -1
    }
    if (firstItem.order < secondItem.order) {
      return 1
    }

    const firstTitle = (firstItem?.company?.title || '').toUpperCase()
    const secondTitle = (secondItem?.company?.title || '').toUpperCase()

    if (firstTitle < secondTitle) return -1
    if (firstTitle > secondTitle) return 1
    return 0
  })

  const numToShow = showExtended ? 6 : 4
  return sorted
    .slice(0, numToShow)
    .sort((firstItem, secondItem) => {
      const firstTitle = (firstItem?.company?.title || '').toUpperCase()
      const secondTitle = (secondItem?.company?.title || '').toUpperCase()

      if (firstTitle < secondTitle) return -1
      if (firstTitle > secondTitle) return 1
      return 0
    })
}

function Person ({ profileColor, _rawBio, name, focus, youtubePlayer, videoId, mobileVideoId, position, department, socialLinkedin, socialInstagram, socialSubstack, socialTwitter, showFeaturedInvestments, showExtendedFeaturedInvestments, relatedCompaniesSort, relatedArticles, personArticles }) {
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' })
  const isDesktopMQ = useMediaQuery({ query: '(min-width: 1024px)' })
  const [isDesktop, setIsDesktop] = useState(true)
  useEffect(() => { setIsDesktop(isDesktopMQ) }, [isDesktopMQ])
  const useDesktopHero = isDesktop && showFeaturedInvestments !== false
  const socials = useMemo(() => getSocialLinks({
    socialTwitter,
    socialLinkedin,
    socialInstagram,
    socialSubstack
  }), [socialTwitter, socialLinkedin, socialInstagram, socialSubstack])
  const featuredCompanies = useMemo(() => {
    if (showFeaturedInvestments === false) return []
    return getFeaturedCompanies(relatedCompaniesSort, showExtendedFeaturedInvestments)
  }, [relatedCompaniesSort, showFeaturedInvestments, showExtendedFeaturedInvestments])
  const departmentText = department?.length > 0
    ? (department === 'founders' ? 'Founders + Emeritus' : department.join(', ').replace('-', ' '))
    : ''

  const handleScrollToCompanies = (e) => {
    e.preventDefault()
    const el = document.getElementById('companies')
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  useEffect(() => {
    const hero = document.querySelector(`.${heroStyles.heroTeamSubpage}`)
    const handleScroll = () => {
      window.requestAnimationFrame(() => {
        const currentScrollY = window.scrollY

        if (!hero) return

        if (currentScrollY > hero.offsetHeight) {
          document.getElementsByTagName('header')[0].classList.remove(headerStyles.redHead)
        } else {
          document.getElementsByTagName('header')[0].classList.add(headerStyles.redHead)
        }
      })
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  return (
    <div className={`${styles.grayishWhiteBackground}`}>
      <Hero className={`${!videoId ? heroStyles.noVideo : ''}`}>
        {useDesktopHero ? (
          <div className={`${heroStyles.heroTeamSubpage} ${styles.desktopHero}`}>
            <div className={styles.desktopHeroContent}>
              <div className={styles.desktopHeroLeft}>
                <div className={styles.desktopHeroName}>
                  <Textfit max={64} mode='single'>{name}</Textfit>
                </div>
                <div className={styles.desktopHeroPosition}>
                  {position}{departmentText ? ',' : ''} <span>{departmentText}</span>
                </div>

                {_rawBio?.length > 0 && (
                  <BlockContent
                    className={styles.desktopHeroBio}
                    blocks={_rawBio}
                  />
                )}

                {socials.length > 0 && (
                  <div className={styles.desktopHeroSocialRow}>
                    {socials.map(({ label, href }) => (
                      <a
                        key={label}
                        href={href}
                        target='_blank'
                        rel='noreferrer'
                        className={styles.desktopHeroSocialLink}
                      >
                        <span className={styles.desktopHeroSocialLinkText}>{label}</span>
                        <Icon symbol='arrowTopRight' />
                      </a>
                    ))}
                  </div>
                )}

                {featuredCompanies.length > 0 && (
                  <section className={styles.desktopHeroFeatured}>
                    <h2 className={styles.desktopHeroFeaturedTitle}>Featured Investments</h2>

                    <div className={`${styles.desktopHeroFeaturedGrid} ${showExtendedFeaturedInvestments ? styles.desktopHeroFeaturedGridExtended : ''}`}>
                      {featuredCompanies.map((item) => {
                        const company = item?.company
                        const logo = company?.logo

                        if (!company || !logo) return null

                        return (
                          <Link to={`/companies/${company.slug.current}/`}
                            key={company._id || company.title}
                            className={styles.desktopHeroFeaturedLogoCell}
                          >
                            <Image
                              imgObj={logo}
                              className={styles.desktopHeroFeaturedLogo}
                              props={{ mode: 'contain' }}
                            />
                          </Link>
                        )
                      })}
                    </div>

                    <a href='#companies' onClick={handleScrollToCompanies} className={styles.desktopHeroFeaturedLink}>
                      View All Companies ↓
                    </a>
                  </section>
                )}
              </div>

              <div className={styles.desktopHeroRight}>
                <div className={styles.desktopHeroMedia}>
                  {(videoId
                    ? <Video
                        addClass={isDesktop ? 'personDesktopHeroVideo' : 'heroTeamSubpageVideo'} type={youtubePlayer ? 'youtube' : 'standard'}
                        videoId={isMobile && mobileVideoId ? mobileVideoId : videoId} mute={1} muteButton={1}
                    />
                    : <img src={profileColor.asset.url} alt='' />
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className={`${heroStyles.heroTeamSubpage}`}>
            <div className={`${heroStyles.heroTeamSubpageName}`}>
              <Textfit max={108} mode='single'>{name}</Textfit>
            </div>
            <div className={`${heroStyles.heroTeamSubpagePosition}`}>
              {position}{departmentText ? ',' : ''} <span>{departmentText}</span>
            </div>
            <div className={`${heroStyles.heroTeamSubpageVideoWrapp} ${!videoId ? heroStyles.noVideo : ''}`}>
              {(videoId
                ? <Video
                    addClass='heroTeamSubpageVideo' type={youtubePlayer ? 'youtube' : 'standard'}
                    videoId={isMobile && mobileVideoId ? mobileVideoId : videoId} mute={1} muteButton={1}
                />
                : <img src={profileColor.asset.url} alt='' />
              )}
            </div>
          </div>
        )}
      </Hero>

      {!useDesktopHero && (
        <Introduction
          focus={focus} content={_rawBio} instagram={socialInstagram} medium={socialSubstack}
          twitter={socialTwitter} linkedIn={socialLinkedin}
        />
      )}

      {/* {personArticles.length > 0 && (
        <NewsPostPreviewGrid
          name={name.split(' ')[0]}
          showSocials={false}
          nodes={personArticles}
          browseMoreHref='/news/'
          showMax={3}
        />
      )} */}

      {!useDesktopHero && (showFeaturedInvestments !== false && relatedCompaniesSort?.length > 0) && (
        <FeaturedInvestments name={name.split(' ')[0]} content={relatedCompaniesSort} showExtended={showExtendedFeaturedInvestments} />
      )}


      {(relatedCompaniesSort?.length > 0) && (
        <InvestmentsTable name={name.split(' ')[0]} content={relatedCompaniesSort} />
      )}

      {(relatedArticles.length > 0 || personArticles.length > 0) && (
        <PersonArticles
          name={name.split(' ')[0]}
          relatedArticles={relatedArticles}
          personArticles={personArticles}
        />
      )}

    </div>
  )
}

export default Person
